import React, { useEffect, useRef, useState } from 'react';
import { useLocale } from '../i18n/LocaleContext';
import { SORT_OPTIONS } from '../utils/sortOptions';
import { QUICK_DATES } from '../utils/filters';
import { musicGroupIcon } from '../utils/musicGroups';
import { CheckIcon, CloseIcon, SearchIcon, SlidersIcon, SortIcon } from './Icons';

function SortMenu({ value, onChange, disabled }) {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="sort" ref={ref}>
      <button
        type="button"
        className="btn"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <SortIcon size={16} />
        {t('sort.label')}
      </button>

      {open && (
        <ul className="sort__menu" role="listbox" aria-label={t('sort.title')}>
          {SORT_OPTIONS.map((option) => (
            <li key={option.id}>
              <button
                type="button"
                role="option"
                aria-selected={value === option.id}
                className={`sort__item${value === option.id ? ' sort__item--active' : ''}`}
                onClick={() => {
                  setOpen(false);
                  onChange(option.id);
                }}
              >
                {t(`sort.${option.id}`)}
                {value === option.id && <CheckIcon size={15} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function DiscoveryBar({
  searchQuery,
  onSearchQueryChange,
  onSubmitSearch,
  categories,
  selectedCategories,
  onToggleCategory,
  quickDate,
  onQuickDate,
  freeOnly,
  onToggleFree,
  sortOptionId,
  onSortChange,
  activeFilterCount,
  onOpenFilters,
  loading,
  musicGroups = [],
  selectedMusicGroups = [],
  onToggleMusicGroup,
  hidden = false
}) {
  const { t } = useLocale();

  // The three genre groups sit right after "Koncert", where a visitor looking
  // for music already is (or lead the row if there is no concert category).
  const musicChips = musicGroups.length > 0 && (
    <>
      <span className="rail__divider" aria-hidden="true" />
      <span className="rail__group" role="group" aria-label={t('filters.music')}>
        {musicGroups.map((group) => {
          const selected = selectedMusicGroups.includes(group.id);
          return (
            <button
              key={group.id}
              type="button"
              className={`chip${selected ? ' chip--selected' : ''}`}
              onClick={() => onToggleMusicGroup(group.id)}
              aria-pressed={selected}
            >
              <span className="chip__icon">{musicGroupIcon(group.id)}</span>
              {t(`music.${group.id}`)}
              <span className="chip__count">{group.count}</span>
            </button>
          );
        })}
      </span>
      <span className="rail__divider" aria-hidden="true" />
    </>
  );
  const hasConcert = categories.some((category) => category.id === 'concert');

  return (
    <div className={`discovery${hidden ? ' discovery--hidden' : ''}`}>
      <div className="page">
        <form
          className="searchbar"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmitSearch();
          }}
          role="search"
        >
          <div className="searchbar__field">
            <span className="searchbar__icon">
              <SearchIcon size={18} />
            </span>
            <input
              type="search"
              className="searchbar__input"
              placeholder={t('search.placeholder')}
              value={searchQuery}
              onChange={(event) => onSearchQueryChange(event.target.value)}
              aria-label={t('search.placeholder')}
            />
            {searchQuery && (
              <button
                type="button"
                className="searchbar__clear"
                onClick={() => onSearchQueryChange('')}
                aria-label={t('search.clear')}
              >
                <CloseIcon size={15} />
              </button>
            )}
          </div>

          <button
            type="button"
            className={`btn btn--filters${activeFilterCount > 0 ? ' btn--active' : ''}`}
            onClick={onOpenFilters}
          >
            <SlidersIcon size={16} />
            {t('filters.open')}
            {activeFilterCount > 0 && (
              <span className="btn__badge">{activeFilterCount}</span>
            )}
          </button>

          <SortMenu value={sortOptionId} onChange={onSortChange} disabled={loading} />
        </form>

        {(categories.length > 0 || musicGroups.length > 0) && (
          <div className="rail" role="group" aria-label={t('filters.category')}>
            {!hasConcert && musicChips}
            {categories.map((category) => {
              const selected = selectedCategories.includes(category.id);
              return (
                <React.Fragment key={category.id}>
                  <button
                    type="button"
                    className={`chip${selected ? ' chip--selected' : ''}`}
                    onClick={() => onToggleCategory(category.id)}
                    aria-pressed={selected}
                  >
                    <span className="chip__icon">{category.icon}</span>
                    {t(`cat.${category.id}`)}
                    <span className="chip__count">{category.count}</span>
                  </button>
                  {category.id === 'concert' && musicChips}
                </React.Fragment>
              );
            })}
          </div>
        )}

        <div className="rail rail--dates" role="group" aria-label={t('filters.date')}>
          {QUICK_DATES.map((id) => (
            <button
              key={id}
              type="button"
              className={`chip chip--sm${quickDate === id ? ' chip--selected' : ''}`}
              onClick={() => onQuickDate(id)}
              aria-pressed={quickDate === id}
            >
              {t(`quick.${id}`)}
            </button>
          ))}
          <button
            type="button"
            className={`chip chip--sm${freeOnly ? ' chip--selected' : ''}`}
            onClick={onToggleFree}
            aria-pressed={freeOnly}
          >
            {t('quick.free')}
          </button>
        </div>
      </div>
    </div>
  );
}
